import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntersectionObserver } from '../useIntersectionObserver';

type ButtonState = 'none' | 'start' | 'all' | 'end';

type Ref = RefObject<Element | null>;
type UseScrollContainerReturn = ModelHookResult<
	{ scrollRightDisabled: boolean; scrollLeftDisabled: boolean; activeIndex: number },
	{ handleScrollEnd: () => void; scrollToRight: () => void; scrollToLeft: () => void }
>;

/**
 * Returns the index encoded in the currently-intersecting entry's id, or 0 if there is none.
 * Each <li> is rendered with id="scroll-container-list-item-{index}" (see ScrollContainerV2), so
 * the currently-intersecting item's own position can be read straight off its id rather than
 * walking the DOM for its sibling index. `id` is read directly off `entry.target` (typed as
 * `Element`, which already declares `id: string`) rather than narrowed via `instanceof
 * HTMLElement` -- that global doesn't exist during SSR, and `instanceof` evaluates its
 * right-hand operand even when the left side is null, throwing ReferenceError server-side
 * (CHANGE-97564).
 */
function getActiveIndex(entry: IntersectionObserverEntry | null): number {
	const match = /-(\d+)$/.exec(entry?.target.id ?? '');
	return match ? Number(match[1]) : 0;
}

// eslint-disable-next-line max-lines-per-function
export function useScrollContainer(scrollContainerRef: Ref): UseScrollContainerReturn {
	const [buttonState, setButtonState] = useState<ButtonState>('none');
	const entry = useIntersectionObserver({ root: scrollContainerRef, threshold: 0.5 });
	const scrollLeftDisabled = buttonState === 'start' || buttonState === 'none';
	const scrollRightDisabled = buttonState === 'end' || buttonState === 'none';
	const activeIndex = useMemo(() => getActiveIndex(entry), [entry]);

	const scrollToLeft = () => {
		if (!scrollContainerRef.current || !entry) return;

		const scrollContainer = scrollContainerRef.current;
		const startPosition = scrollContainer.scrollLeft;

		const currentCard = entry.target;
		const previousCard = currentCard.previousElementSibling;
		const scrollAmount = previousCard ? previousCard.scrollWidth : currentCard.scrollWidth;

		scrollContainerRef.current.scrollTo({
			left: startPosition - scrollAmount,
			behavior: 'smooth',
		});
	};

	const scrollToRight = () => {
		if (!scrollContainerRef.current || !entry) return;

		const scrollContainer = scrollContainerRef.current;
		const startPosition = scrollContainer.scrollLeft;

		const currentCard = entry.target;
		const nextCard = currentCard.nextElementSibling;
		const scrollAmount = nextCard ? nextCard.clientWidth : currentCard.clientWidth;

		scrollContainer.scrollTo({
			left: startPosition + scrollAmount,
			behavior: 'smooth',
		});
	};

	const getButtonState = useCallback((): ButtonState => {
		const scrollContainer = scrollContainerRef.current;
		if (!scrollContainer) return buttonState;

		const isScrollable = scrollContainer.scrollWidth !== scrollContainer.clientWidth;
		if (!isScrollable) return 'none';

		const isStartOfScroll = scrollContainer.scrollLeft === 0;
		if (isStartOfScroll) return 'start';

		// offset calculation is sometimes off by one, so we check both scenarios
		const offset = scrollContainer.scrollWidth - scrollContainer.clientWidth;
		const isEndOfScroll = offset === scrollContainer.scrollLeft || offset + 1 === scrollContainer.scrollLeft;
		if (isEndOfScroll) return 'end';

		return 'all';
	}, [buttonState, scrollContainerRef]);

	// initialize button state
	useEffect(() => {
		if (scrollContainerRef.current) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setButtonState(getButtonState());
		}
	}, [getButtonState, scrollContainerRef]);

	const handleScrollEnd = () => {
		setButtonState(getButtonState());
	};

	return {
		data: { scrollLeftDisabled, scrollRightDisabled, activeIndex },
		actions: { handleScrollEnd, scrollToRight, scrollToLeft },
	};
}
